import React, { createContext, useContext, useEffect } from "react";
import { getRegionConfig, RegionConfig } from "@/lib/regionConfig";

interface RegionContextValue {
  region: RegionConfig;
  formatCurrency: (amount: number, currencyCode?: string) => string;
}

const RegionContext = createContext<RegionContextValue | null>(null);

export const RegionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const region = getRegionConfig();

  useEffect(() => {
    document.documentElement.setAttribute("data-region", region.code);
  }, [region.code]);

  const formatCurrency = (amount: number, currencyCode?: string): string => {
    const symbol = currencyCode
      ? formatCurrencySymbolFromCode(currencyCode)
      : region.currencySymbol;
    return `${symbol} ${amount.toFixed(2)}`;
  };

  return (
    <RegionContext.Provider value={{ region, formatCurrency }}>
      {children}
    </RegionContext.Provider>
  );
};

export function useRegion(): RegionContextValue {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error("useRegion must be used within a RegionProvider");
  }
  return context;
}

function formatCurrencySymbolFromCode(code: string): string {
  const map: Record<string, string> = {
    MYR: "RM",
    THB: "฿",
  };
  return map[code] || code;
}
